import { Fragment, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { observer } from 'mobx-react-lite';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { FormLabel } from '@components/form-label';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconBrandOneSystems } from '@components/icons/brand-onesystems';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { InfoBox } from '@components/info-box';
import { IntegrationCard } from '@components/integration-card';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { abbreviateDate } from '@utils/date';
import { isRequestError } from '@utils/requests';

import { Loading } from 'components/Loading';
import { useStore } from 'models/Provider';

import {
  createOnboardingCase,
  loadOnboardingCase,
  loadOneCRMJobCase,
  OneCRMOnboardingCase,
  OneCRMJobCase,
} from './api';
import { FileStatus } from './file-status';

export const OneCRMOnboardingPage = observer(function OneCRMOnboardingPage() {
  const { jobStore, t } = useStore();
  const { jobID } = useParams();

  const jobCaseQuery = useQuery({
    queryKey: ['onecrm-job-case', jobID],
    queryFn: () => loadOneCRMJobCase(Number(jobID!)),
  });

  const job = jobStore.items.get(jobID);
  const jobIntegration = job.integrations.find((integration: any) => integration.name === 'OneCRM');
  const hasNoCase = jobCaseQuery.isSuccess && jobCaseQuery.data.case === null;

  useEffect(() => {
    job.loadIntegrations();
  }, [job]);

  return (
    <Fragment>
      <Stack space={6}>
        <IntegrationCard
          icon={IconBrandOneSystems}
          header="OneCRM"
          text={t('integrations.onecrm.onboarding.title')}
        />
        {hasNoCase && (
          <Stack space={4} align="flex-end">
            <InfoBox variant="orange" text={t('integrations.onecrm.onboarding.nojobcase')} />
            <Link
              to={`/jobs/${jobID}/integrations/${jobIntegration ? jobIntegration.triggerId : ''}`}
            >
              <Button
                variant="secondary"
                iconLeft={<IconOpenNewTab />}
                text={t('integrations.onecrm.onboarding.gojobapp')}
              />
            </Link>
          </Stack>
        )}
        {jobCaseQuery.data?.case && <JobCase jobCase={jobCaseQuery.data.case} />}
        {jobCaseQuery.data?.case && <OnboardingCase />}
      </Stack>
    </Fragment>
  );
});

function OnboardingCase() {
  const { appID } = useParams();

  const onboardingCaseQuery = useQuery({
    queryKey: ['onecrm-onboarding-case', appID],
    queryFn: () => loadOnboardingCase(Number(appID)),
  });

  if (!onboardingCaseQuery.data) return <Loading delay={1000} />;

  return (
    <AnimatePresence exitBeforeEnter initial={false}>
      <motion.div
        key={String(onboardingCaseQuery.data?.case?.transferCompleted)}
        animate={{ opacity: 1, x: 0 }}
        initial={{ opacity: 0, x: 40 }}
        exit={{ opacity: 0, x: -40 }}
        transition={{ duration: 0.15 }}
      >
        {onboardingCaseQuery.data.case?.transferCompleted ? (
          <OnboardingCaseSuccess data={onboardingCaseQuery.data.case} />
        ) : (
          <CreateOnboardingCase />
        )}
      </motion.div>
    </AnimatePresence>
  );
}

function OnboardingCaseSuccess({ data }: { data: OneCRMOnboardingCase }) {
  const { t, lang } = useStore();
  return (
    <Card space={6} variant="default">
      <Stack space={6}>
        <Text font="bold16" text={t('integrations.onecrm.onboarding.create.title')} />
        <Stack space={1}>
          <Text font="semibold14" text="Onboarding form (PDF)" />
          <FileStatus status={data.onboardingFileUploadStatus} />
        </Stack>
        <Separator />
        <Stack space={1}>
          <Text font="semibold14" text={t('integrations.onecrm.jobcase.createdby')} />
          <div>
            <Text font="regular16" text={data.createdBy.name} />
            {data.transferCompleted && (
              <Text font="regular16" text={abbreviateDate(data.transferCompleted, lang)} />
            )}
          </div>
        </Stack>
      </Stack>
    </Card>
  );
}

function CreateOnboardingCase() {
  const queryClient = useQueryClient();
  const { appID } = useParams();
  const { t } = useStore();
  const { showError, showSuccess } = useToaster();

  const createMutation = useMutation(createOnboardingCase, {
    onSuccess() {
      queryClient.invalidateQueries(['application-onboarding-load', appID]);
      queryClient.invalidateQueries(['onecrm-onboarding-case', appID]);
      showSuccess(t('integrations.onecrm.onboarding.create.success'));
    },
    onError(data) {
      if (
        !isRequestError(data) ||
        (isRequestError(data) && data.response?.status !== 400) ||
        !data.response?.data
      ) {
        showError(t('integrations.onecrm.onboarding.create.error'));
        return;
      }

      showError(data.response.data);
    },
  });

  return (
    <Fragment>
      <Card variant="transparent" space={6}>
        {createMutation.isLoading && <Loading />}
        <Stack space={6}>
          <Text font="bold16" text={t('integrations.onecrm.onboarding.create.title')} />
          <div>
            <FormLabel text={t('integrations.onecrm.onboarding.create.label')} />
            <Text font="regular16" text="Onboarding form (PDF)" />
          </div>
          <Stack space={2} align="flex-end">
            <Button
              onClick={() => createMutation.mutate(Number(appID))}
              isDisabled={createMutation.isLoading}
              iconLeft={<IconAddPlus />}
              variant="primary"
              text={t('integrations.onecrm.onboarding.create.action')}
            />
          </Stack>
        </Stack>
      </Card>
    </Fragment>
  );
}

function JobCase({ jobCase }: { jobCase: OneCRMJobCase }) {
  const { t, lang } = useStore();

  return (
    <Card variant="default" space={6}>
      <Stack space={6}>
        <Text font="bold16" text={t('integrations.onecrm.onboarding.jobcase.header')} />
        <Stack space={4}>
          <Stack space={1}>
            <Text font="semibold14" text={t('integrations.onecrm.jobcase.title')} />
            <Text font="regular16" text={jobCase.caseName} />
          </Stack>
          <Stack space={1}>
            <Text font="semibold14" text={t('integrations.onecrm.jobcase.manager')} />
            <Text font="regular16" text={jobCase.manager.name} />
          </Stack>
        </Stack>
        <Separator />
        <Stack space={1}>
          <Text font="semibold14" text={t('integrations.onecrm.jobcase.createdby')} />
          <div>
            <Text font="regular16" text={jobCase.manager.name} />
            <Text font="regular16" text={abbreviateDate(jobCase.dateCreated, lang)} />
          </div>
        </Stack>
      </Stack>
    </Card>
  );
}
